import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Clock, Globe, Lock, Mail, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage, apiErrorType, MODULE_NOT_ENTITLED_TYPE } from "@/lib/api-error";

/** The backend's answer for an invoice that has never been sent electronically. */
const DELIVERY_NOT_FOUND_TYPE = "EInvoicing.DeliveryNotFound";
import { moduleForbiddenMessage } from "@/lib/modules";
import type { components } from "@dolfin/api-public";

import { DeliveryStateBadge, IN_FLIGHT } from "@/components/EInvoiceDeliveryState";
import { useInvoiceDeliveryChange } from "@/hooks/useInvoiceDeliveryChange";
import { formatDateTime } from "@/lib/format";
import { Timeline } from "@/components/PageUi";

type Delivery = components["schemas"]["OutboundEInvoiceView"];
type NetworkEvent = components["schemas"]["OutboundNetworkEvent"];
type InvoiceEmail = components["schemas"]["InvoiceEmailResponse"];

/** Deliveries settle in about a second; polling covers a slow or lost provider callback. */
const POLL_MS = 2000;

/** Past this, asking faster won't help: the backend sweep settles the row whether or not this tab is open. */
const POLL_TIMEOUT_MS = 120_000;

interface Props {
  invoiceId: string;
  /** Invoice status, because only an issued invoice can be sent over a network. */
  invoiceStatus?: string;
  /** Whether the signed-in user may send. Same gate as the email send. */
  canSend: boolean;
  /**
   * Passed in: the component can't see the host's entitlements. Without the module no request is made, as the
   * 403 would raise a global toast on every invoice.
   */
  enabled?: boolean;
  /** The send chose the network: the delivery appears by itself a moment later. */
  deliverAsEInvoice?: boolean;
  /** Where the PDF copy goes once the network confirms delivery (InvoiceResponse.pdfCopyTo). */
  pdfCopyTo?: string | null;
  /** The SignalR host, for live updates. Without it the card falls back to polling. */
  hubBaseUrl?: string;
  /** Null while unknown; unregistered, the send is disabled with the reason. */
  registered?: boolean | null;
  /** Opens the organisation's e-invoicing settings, from the not-registered note. */
  onEInvoicingSettingsClick?: () => void;
}

/**
 * Follows the delivery live and offers a send only as recovery (issued before the org could e-invoice, or
 * abandoned). Without the module the button shows locked, not hidden, and no request is made.
 */
export function EInvoiceDelivery({
  invoiceId,
  invoiceStatus,
  canSend,
  enabled = true,
  deliverAsEInvoice = false,
  pdfCopyTo,
  hubBaseUrl,
  registered = true,
  onEInvoicingSettingsClick,
}: Props) {
  const api = useDolfinClient();
  const [delivery, setDelivery] = useState<Delivery | null>(null);
  const [emails, setEmails] = useState<InvoiceEmail[]>([]);
  const [entitled, setEntitled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // The send chose the network but no delivery appeared within the poll window: the dispatch was refused
  // or never ran. Stop saying "handing over" and offer the send, which is the way back.
  const [handOverTimedOut, setHandOverTimedOut] = useState(false);
  const pollingSince = useRef<number | null>(null);
  // A read that returns after navigation belongs to the previous invoice; painting it would show its status here.
  const showing = useRef(invoiceId);

  const issued = invoiceStatus !== undefined && invoiceStatus !== "Draft" && invoiceStatus !== "Void";

  const load = useCallback(async () => {
    if (!enabled) return null;

    const requested = invoiceId;
    const { data, error } = await api.GET("/v1/invoices/{id}/e-invoice", {
      params: { path: { id: invoiceId } },
    });

    if (requested !== showing.current) return null;

    if (error) {
      // Three different things arrive as "error" and only two of them mean anything here.
      if (apiErrorType(error).includes(MODULE_NOT_ENTITLED_TYPE)) {
        // This client does not have e-invoicing at all, so the section is not empty, it is not theirs.
        setEntitled(false);
        setDelivery(null);
      } else if (apiErrorType(error).includes(DELIVERY_NOT_FOUND_TYPE)) {
        // Never sent electronically. The only case where "no delivery" is the truth.
        setDelivery(null);
      }
      // Anything else is a failed READ: keep the last known state rather than blank it and re-offer E-send.
      return null;
    }

    setDelivery(data ?? null);
    return data ?? null;
  }, [api, invoiceId, enabled]);

  useEffect(() => {
    showing.current = invoiceId;
    // The router reuses this component across invoices, so reset the polling budget or the next invoice inherits
    // an exhausted one and says "waiting" forever.
    pollingSince.current = null;
    setHandOverTimedOut(false);

    let cancelled = false;
    setLoading(true);
    load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [load, invoiceId]);

  // The PDF copy is the last step of an e-invoiced send, so its email is part of this card.
  const loadEmails = useCallback(async () => {
    if (!enabled || !deliverAsEInvoice || !pdfCopyTo) return;
    const requested = invoiceId;
    const { data } = await api.GET("/v1/invoices/{id}/emails", { params: { path: { id: invoiceId } } });
    if (requested !== showing.current) return;
    setEmails(data ?? []);
  }, [api, invoiceId, enabled, deliverAsEInvoice, pdfCopyTo]);

  useEffect(() => {
    void loadEmails();
  }, [loadEmails]);

  // Live: the network's verdict (which for SDI can take hours) and the PDF copy it releases arrive as
  // they happen, with no polling budget to run out.
  useInvoiceDeliveryChange(
    invoiceId,
    enabled && issued,
    () => {
      void load();
      void loadEmails();
    },
    { hubBaseUrl },
  );

  // A send that chose the network creates its delivery a moment later, from a consumer. Until it lands,
  // look for it rather than offering to send something that is already on its way.
  useEffect(() => {
    if (!enabled || !issued || !deliverAsEInvoice || delivery) return;
    const timer = setInterval(() => void load(), POLL_MS);
    const stop = setTimeout(() => {
      clearInterval(timer);
      setHandOverTimedOut(true);
    }, POLL_TIMEOUT_MS);
    return () => {
      clearInterval(timer);
      clearTimeout(stop);
    };
  }, [enabled, issued, deliverAsEInvoice, delivery, load]);

  // Watch an in-flight delivery until it settles. The interval is cleared on any terminal state, so a
  // delivered invoice costs exactly one extra request.
  useEffect(() => {
    if (!delivery?.state || !IN_FLIGHT.includes(delivery.state)) {
      pollingSince.current = null;
      return;
    }

    pollingSince.current ??= Date.now();

    const timer = setInterval(async () => {
      if (pollingSince.current && Date.now() - pollingSince.current > POLL_TIMEOUT_MS) {
        clearInterval(timer);
        return;
      }
      await load();
    }, POLL_MS);

    return () => clearInterval(timer);
  }, [delivery?.state, load]);

  async function send(mode: "send" | "resend") {
    if (sending) return;
    setSending(true);
    try {
      const path = mode === "send" ? "/v1/invoices/{id}/e-invoice/send" : "/v1/invoices/{id}/e-invoice/resend";
      const { data, error } = await api.POST(path, { params: { path: { id: invoiceId } } });

      if (error || !data) {
        // The provider's validation messages are field-precise ("customer tax id: invalid check
        // digits"), so they are shown as they came rather than replaced with wording of ours.
        toast.error(apiErrorMessage(error, "Could not send this invoice over the network."));
        // A refused send is recorded on the delivery, with its reason: show it.
        void load();
        return;
      }

      setDelivery(data);
      // A fresh document gets a fresh polling budget.
      pollingSince.current = null;
      if (data.state === "Delivered") toast.success("Delivered.");
      else if (data.state === "DeliveryFailed") toast.error("The network refused the document.");
      else if (data.state === "Accepted") toast.success("The network has the document. Waiting for its verdict.");
      else toast.success("Sent to the network. Waiting for confirmation.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not send this invoice over the network.");
    } finally {
      setSending(false);
    }
  }

  // The network could not find the customer: the PDF can still reach them by email, now, while the
  // e-invoice waits for a corrected address. Not offered for SDI, whose verdict must precede the PDF.
  async function emailInstead() {
    if (sending) return;
    setSending(true);
    try {
      const { error } = await api.POST("/v1/invoices/{id}/send", {
        params: { path: { id: invoiceId } },
        body: { email: "Now" },
      });
      if (error) {
        toast.error(apiErrorMessage(error, "Could not email the invoice."));
        return;
      }
      toast.success("Emailing the PDF to the customer.");
      void loadEmails();
    } finally {
      setSending(false);
    }
  }

  // Locked, not absent: the feature is advertised and explained rather than silently missing.
  if (!enabled || !entitled) return <LockedESend />;
  if (loading) return null;

  const state = delivery?.state;
  const inFlight = state !== undefined && IN_FLIGHT.includes(state);

  // The send chose the network and its delivery has not appeared yet: it is being handed over.
  const handingOver = issued && deliverAsEInvoice && !delivery && !handOverTimedOut;
  // Offered only where it is the way back: an invoice sent before its organisation could e-invoice, or a
  // send abandoned before anything reached the network. A normal send needs no second button.
  const offerSend = issued && !handingOver && (!delivery || state === "Abandoned") && registered !== null;
  const offerResend = state === "DeliveryFailed" && !!delivery?.canResend && registered !== null;
  // The send goes to the network as this organisation, so without a registration there is nothing to
  // send from. The buttons stay, disabled, so the reason sits where the user was about to click.
  const notRegistered = registered === false;

  return (
    <div className="flex flex-col gap-2">
      {handingOver ? (
        <div className="flex items-center gap-2 rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
          <Spinner className="size-4" />
          Handing the invoice to the e-invoicing network…
        </div>
      ) : null}

      {delivery && state ? <DeliveryStatus delivery={delivery} /> : null}

      {deliverAsEInvoice && delivery && state ? (
        <PdfCopyStep state={state} network={delivery.network} to={pdfCopyTo} emails={emails} />
      ) : null}

      {offerSend && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="w-full">
              <Button
                variant="outline"
                size="lg"
                className="w-full text-base font-semibold"
                disabled={!issued || !canSend || notRegistered || sending}
                onClick={() => send(state === "Abandoned" ? "resend" : "send")}
              >
                {sending ? <Spinner className="size-4" /> : <Globe className="size-5" />}
                {sending ? "Sending..." : state === "Abandoned" ? "Send again over the network" : "Send as e-invoice"}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {!canSend
              ? "Only owners and admins can send invoices"
              : notRegistered
                ? "This organisation is not registered for e-invoicing"
                : state === "Abandoned"
                  ? "Nothing reached the network last time, so sending again is safe"
                  : "This invoice was not sent over the e-invoicing network. Send it there now."}
          </TooltipContent>
        </Tooltip>
      )}

      {/* Failed or abandoned, and sending again can help: a corrected document when nothing reached the
          network, the same document when the recipient may already hold it. */}
      {offerResend && delivery && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="w-full">
              <Button
                variant="outline"
                size="lg"
                className="w-full text-base font-semibold"
                disabled={!canSend || notRegistered || sending}
                onClick={() => send("resend")}
              >
                {sending ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
                {sending ? "Resending..." : delivery.resendRebuildsDocument ? "Resend with current details" : "Resend"}
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {notRegistered
              ? "This organisation is not registered for e-invoicing"
              : delivery.resendRebuildsDocument
                ? "Nothing reached the network, so the document is rebuilt from the customer's and your organisation's details as they are now. Fix them first."
                : "The customer may already have this document, so the same one is sent again."}
          </TooltipContent>
        </Tooltip>
      )}

      {notRegistered && (offerSend || offerResend) && (
        <p className="text-center text-xs text-muted-foreground">
          Register this organisation for e-invoicing to send invoices over the network.
          {onEInvoicingSettingsClick ? (
            <>
              {" "}
              <button
                type="button"
                className="font-medium text-foreground underline underline-offset-2 hover:no-underline"
                onClick={onEInvoicingSettingsClick}
              >
                E-invoicing settings
              </button>
            </>
          ) : null}
        </p>
      )}

      {state === "DeliveryFailed" && delivery?.failureKind === "RecipientUnreachable" && delivery.network === "Peppol" && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full"
          disabled={!canSend || sending}
          onClick={() => void emailInstead()}
        >
          <Mail className="size-4" />
          Email the PDF instead
        </Button>
      )}

      {inFlight && state !== "Accepted" && (
        <p className="text-center text-xs text-muted-foreground">
          Waiting for the network to confirm. This page updates on its own.
        </p>
      )}
    </div>
  );
}

/** A disabled button swallows the pointer events its tooltip needs, so the trigger is a wrapping div. */
function LockedESend() {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="w-full">
          <Button variant="outline" size="lg" className="w-full text-base font-semibold" disabled>
            <Lock className="size-4" />
            E-send
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{moduleForbiddenMessage("ar.e_invoicing")}</TooltipContent>
    </Tooltip>
  );
}

function DeliveryStatus({ delivery }: { delivery: Delivery }) {
  const {
    state, network, country, sandbox, faults = [], attempt, deliveredAt, failureKind,
    recipientScheme, recipientAddress, acceptedAt, madeAvailableAt, canResend, resendRebuildsDocument,
    networkEvents = [],
  } = delivery;
  const networkName = network === "Sdi" ? "SDI" : network === "Peppol" ? "Peppol" : "the network";

  // Named channel plus address: the address alone does not say which network it belongs to.
  const recipient = recipientAddress
    ? `${recipientScheme === "it-sdi-code" ? "SDI code" : recipientScheme === "it-sdi-pec" ? "PEC" : "Peppol"} ${recipientAddress}`
    : null;

  return (
    <div className="rounded-lg border bg-muted/30 p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <DeliveryStateBadge state={state} size="md" />
        {network && (
          <Badge variant="secondary" className="gap-1 font-normal">
            {network === "Sdi" ? "SDI" : "Peppol"}
            {country ? ` · ${country}` : ""}
          </Badge>
        )}
        {/* A document that reached a simulator and not a tax authority must say so, or a successful
            test looks exactly like a filed invoice. */}
        {sandbox && (
          <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 font-normal text-amber-700 dark:text-amber-400">
            Test
          </Badge>
        )}
        {Number(attempt) > 1 && <span className="text-xs text-muted-foreground">attempt {attempt}</span>}
      </div>

      {recipient && <p className="mt-2 text-xs text-muted-foreground">Sent to {recipient}</p>}

      {state === "Accepted" && (
        <p className="mt-2 text-xs text-muted-foreground">
          {networkName} took charge of the document
          {acceptedAt ? ` on ${formatDateTime(acceptedAt)}` : ""} and has not ruled yet. A
          rejection usually arrives within minutes; confirmation of delivery can take up to five days.
        </p>
      )}

      {state === "Delivered" && deliveredAt && (
        <p className="mt-2 text-xs text-muted-foreground">
          Delivered to the customer on {formatDateTime(deliveredAt)}. This confirms delivery,
          not that the customer has approved the invoice.
        </p>
      )}

      {/* Issued, not delivered: legally the invoice exists, so the job is telling the customer, not
          sending it again. */}
      {state === "MadeAvailable" && (
        <p className="mt-2 text-xs text-muted-foreground">
          {networkName} could not deliver it
          {madeAvailableAt ? ` (${formatDateTime(madeAvailableAt)})` : ""}, but the invoice is
          issued and held for the customer to collect
          {network === "Sdi" ? " from their Fatture e Corrispettivi area on the tax authority's portal" : ""}.
          Let them know where to find it. Do not send it again.
        </p>
      )}

      {faults.length > 0 && (
        <ul className="mt-2 space-y-1">
          {faults.map((fault, i) => (
            <li key={`${fault.code ?? ""}-${i}`} className="flex gap-1.5 text-xs text-muted-foreground">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
              {/* The stage and the code are shown alongside the message rather than folded into it:
                  the message is what a person reads, the code is what they quote to support. */}
              <span>
                {fault.step ? <span className="font-medium">{fault.step}: </span> : null}
                {fault.message}
                {fault.code ? <span className="opacity-70"> ({fault.code})</span> : null}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* A rejected document cannot be retried — saying why beats leaving the missing button
          unexplained. */}
      {state === "DeliveryFailed" && failureKind === "DocumentRejected" && (
        <p className="mt-2 text-xs text-muted-foreground">
          {canResend && resendRebuildsDocument
            ? `${networkName} rejected the document, so nothing was issued. Fix what it objected to (the customer's details or routing code, for instance), then resend.`
            : "The network refused the document itself, so sending it again would fail the same way. Issue a credit note and a corrected invoice."}
        </p>
      )}

      {networkEvents.length > 0 && <NetworkTimeline events={networkEvents} />}

      {state === "Abandoned" && (
        <p className="mt-2 text-xs text-muted-foreground">
          Nothing reached the network. Fix what the reason above describes, then send again.
        </p>
      )}
    </div>
  );
}

/** The PDF copy goes out only once the network confirms the invoice (Delivered or MadeAvailable). */
function PdfCopyStep({
  state,
  network,
  to,
  emails,
}: {
  state: NonNullable<Delivery["state"]>;
  network?: Delivery["network"];
  to?: string | null;
  emails: InvoiceEmail[];
}) {
  const networkName = network === "Sdi" ? "SDI" : network === "Peppol" ? "Peppol" : "the network";
  if (!to) return null;

  const confirmed = state === "Delivered" || state === "MadeAvailable";
  const sent = emails.find((e) => e.status !== "Failed");

  let icon = <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" />;
  let text: string;
  if (sent && (sent.status === "Bounced" || sent.status === "Inactive" || sent.status === "SpamComplaint")) {
    icon = <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />;
    text = `The PDF copy to ${to} was not delivered (${sent.status.toLowerCase()}). The e-invoice itself is unaffected.`;
  } else if (sent) {
    icon = <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />;
    const when = sent.sentAt ?? sent.createdAt;
    text = `PDF copy emailed to ${to}${when ? ` on ${formatDateTime(when)}` : ""}.`;
  } else if (confirmed) {
    text = `Emailing the PDF copy to ${to}…`;
  } else if (state === "DeliveryFailed" || state === "Abandoned") {
    icon = <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />;
    text = "The customer has not received this invoice yet. Its PDF copy goes out once the network delivers it.";
  } else {
    text = `The PDF copy is emailed to ${to} once ${networkName} confirms delivery.`;
  }

  return (
    <div className="flex items-start gap-2 rounded-lg border bg-muted/30 p-3 text-sm">
      {icon}
      <p className="text-muted-foreground">{text}</p>
    </div>
  );
}

/** The network's own code (SDI's RC, NS, MC...) sits beside each line for quoting to support. */
function NetworkTimeline({ events }: { events: NetworkEvent[] }) {
  // The shared rail reads newest first, so where the delivery stands now is the filled dot.
  const entries = [...events].reverse().map((e, i) => ({
    id: e.id ?? String(i),
    at: e.at,
    title: (
      <>
        {e.description ?? e.outcome}
        {e.source === "Recipient" ? " (customer)" : ""}
      </>
    ),
    detail: e.networkCode ? <span className="font-mono text-xs">{e.networkCode}</span> : null,
  }));
  return (
    <div className="mt-3 border-t pt-3">
      <Timeline entries={entries} />
    </div>
  );
}
